import { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Calendar, Archive, ChevronDown, ChevronUp } from "lucide-react";
import type { TaskDestination } from "@/types";
import { TagInput } from "@/components/TagInput";

interface AddTaskFormProps {
    onAdd: (
        name: string,
        minutes: number,
        destination: TaskDestination,
        tags: string[]
    ) => void;
    allTags?: string[]; // All existing tags for autocomplete
}

export function AddTaskForm({ onAdd, allTags = [] }: AddTaskFormProps) {
    const [name, setName] = useState("");
    const [minutes, setMinutes] = useState("");
    const [tags, setTags] = useState<string[]>([]);
    const [showTags, setShowTags] = useState(false);
    const [destination, setDestination] = useState<TaskDestination>(() => {
        const saved = localStorage.getItem("taskmaster-default-destination");
        return (saved as TaskDestination) || "today";
    });

    useEffect(() => {
        localStorage.setItem("taskmaster-default-destination", destination);
    }, [destination]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim() && minutes) {
            onAdd(name.trim(), parseInt(minutes), destination, tags);
            setName("");
            setMinutes("");
            setTags([]);
        }
    };

    const quickTimes = [15, 25, 30, 45, 60];

    return (
        <Card className="border-dashed border-2 hover:border-primary/50 transition-colors">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Plus className="w-4 h-4 text-primary" />
                    </div>
                    New Task
                </CardTitle>
                <CardDescription>
                    Add a task with an estimated time to complete
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <form onSubmit={handleSubmit} className="space-y-3">
                    <Input
                        placeholder="What are you working on?"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-11"
                    />
                    <div className="flex gap-2 items-center flex-wrap">
                        <div className="relative w-24">
                            <Input
                                type="number"
                                placeholder="Min"
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                className="h-11 pr-10"
                                min="1"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                min
                            </span>
                        </div>
                        <div className="flex gap-1">
                            {quickTimes.map((time) => (
                                <Button
                                    key={time}
                                    type="button"
                                    variant={
                                        minutes === time.toString()
                                            ? "default"
                                            : "outline"
                                    }
                                    size="sm"
                                    className="h-9 w-9 p-0 text-xs"
                                    onClick={() => setMinutes(time.toString())}
                                >
                                    {time}
                                </Button>
                            ))}
                        </div>
                        <Button
                            type="submit"
                            disabled={!name.trim() || !minutes}
                            className="h-11 px-6 ml-auto"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                        </Button>
                    </div>
                </form>

                {/* Destination Toggle */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span className="text-xs text-muted-foreground">
                        Add to:
                    </span>
                    <div className="flex-1 flex gap-1">
                        <Button
                            type="button"
                            variant={
                                destination === "today" ? "default" : "ghost"
                            }
                            size="sm"
                            className="flex-1 h-8 text-xs"
                            onClick={() => setDestination("today")}
                        >
                            <Calendar className="w-3 h-3 mr-1" />
                            Today
                        </Button>
                        <Button
                            type="button"
                            variant={
                                destination === "backlog" ? "default" : "ghost"
                            }
                            size="sm"
                            className="flex-1 h-8 text-xs"
                            onClick={() => setDestination("backlog")}
                        >
                            <Archive className="w-3 h-3 mr-1" />
                            Backlog
                        </Button>
                    </div>
                </div>

                {/* Tags Section */}
                <div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2 mb-2"
                        onClick={() => setShowTags(!showTags)}
                    >
                        {showTags ? (
                            <ChevronUp className="w-3 h-3 mr-1" />
                        ) : (
                            <ChevronDown className="w-3 h-3 mr-1" />
                        )}
                        {tags.length > 0 ? `Tags (${tags.length})` : "Add Tags"}
                    </Button>
                    {showTags && (
                        <TagInput
                            tags={tags}
                            onChange={setTags}
                            placeholder="Add tags..."
                            allTags={allTags}
                        />
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
